import React, { useState, useEffect, useCallback , useMemo} from "react";
import backgroundImg from "../assets/instructions-bg.png"; 
import { Link } from "react-router-dom";
import { useAudio } from "../components/AudioContext.js";
import '../styles/game.css';
import Seo from './Seo';

export default function Game() {
  const { startAudio } = useAudio(); // Use the context
  
    useEffect(() => {
      startAudio(); // Start audio when component mounts
    }, [startAudio]);


  const [showOptions, setShowOptions] = useState(false);
  const [currentLine, setCurrentLine] = useState({ text: '', visible: false, position: 0 });

  // Define the text lines to animate
  const textLines = useMemo(() => [
    "Check in with friends as the stay begins like any other",
    "But the unexpected can strike anytime, anywhere",
    "Can your group solve the mystery before time runs out?",
    "Your actions decide how the mystery unravels"
  ],[]);

  // Function to restart the animation
  const restartAnimation = () => {
    setShowOptions(false);
    setCurrentLine({ text: '', visible: false, position: 0 });
    
    // Start animation again after a brief delay
    setTimeout(() => {
      animateLines();
    }, 500);
  };

  const animateLines = useCallback(async () => {
  await new Promise(resolve => setTimeout(resolve, 1000));

  for (let i = 0; i < textLines.length; i++) {
    if (i > 0) {
      setCurrentLine(prev => ({ ...prev, visible: false }));
      await new Promise(resolve => setTimeout(resolve, 2100));
    }

    setCurrentLine({
      text: textLines[i],
      visible: true,
      position: i
    });

    await new Promise(resolve => setTimeout(resolve, 2100));
  }

  setCurrentLine(prev => ({ ...prev, visible: false }));
  await new Promise(resolve => setTimeout(resolve, 1000));
  setShowOptions(true);
}, [textLines]);

  useEffect(() => {
    animateLines();
  }, [animateLines]);

  return (
    <div className="page-game">
      <Seo
        path="/game"
        title="How Mystery Mansion Works | Real-Life Mystery Game"
        description="Discover how Mystery Mansion works — an open-world mystery experience where your group investigates clues, solves puzzles and uncovers a story during your villa stay."
      />

      
      <div 
        className="min-h-screen bg-cover bg-center flex flex-col items-center justify-center px-4 sm:px-8 md:px-16"
        style={{ 
          backgroundImage: `url(${backgroundImg})`,
          filter: 'brightness(1.2)'
        }}
      >
        {/* Main content container */}
        <div className="relative z-10 text-white w-full text-center max-w-10xl mx-auto">
          {/* Title with wavy animation */}
          <h1 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 md:mb-10 justify-center"
            style={{ fontFamily: "Avenir", marginTop: '1rem sm:2rem' }}
          >
            {"About the Game".split('').map((letter, index) => (
              <span key={index} className="wavy-letter">
                {letter === ' ' ? '\u00A0' : letter}
              </span>
            ))}
          </h1>

          {/* Text animation container */}
          <div 
            className="min-h-[300px] sm:min-h-[350px] md:min-h-[400px] flex flex-col items-center justify-start mb-8 sm:mb-12 md:mb-16"
            style={{ fontFamily: "Avenir", paddingTop: '1rem sm:1.5rem md:2rem' }}
          >
            {/* INCREASED SPACING CALCULATION */}
            <div style={{ height: `${currentLine.position * (window.innerWidth < 640 ? 80 : window.innerWidth < 768 ? 100 : 120)}px` }}></div>
            <p 
              className="text-lg sm:text-xl md:text-3xl lg:text-4xl xl:text-5xl text-center max-w-sm sm:max-w-lg md:max-w-6xl lg:max-w-full xl:max-w-full leading-relaxed px-4 whitespace-nowrap"
              style={{ 
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                opacity: currentLine.visible ? 1 : 0,
                transition: 'opacity 2s ease-out',
                fontWeight: 'bold'
              }}
            >
              {currentLine.text}
            </p>
          </div>

          {/* Two Options - Side by Side */}
          <div 
            className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8 md:gap-12"
            style={{ 
              opacity: showOptions ? 1 : 0,
              transition: 'opacity 1.2s ease-out'
            }}
          >
            {/* See Hints Again Button */}
            <button 
              onClick={restartAnimation}
              className="bg-gray-700 bg-opacity-70 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full hover:bg-gray-900 transition-all duration-300 flex items-center space-x-2 text-sm sm:text-base"
              style={{ 
                fontFamily: "Avenir"
              }}
            >
              <span>↻</span>
              <span>See Hints Again</span>
            </button>

            {/* Continue Button */}
            <Link to="/locations">
              <button 
                className="bg-gray-700 bg-opacity-70 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full hover:bg-gray-900 transition-all duration-300 flex items-center space-x-2 text-sm sm:text-base"
                style={{ 
                  fontFamily: "Avenir"
                }}
              >
                <span>Continue</span>
                <span>→</span>
              </button>
            </Link>
          </div>

          {/* Optional helper text */}
          <p 
            className="text-xs sm:text-sm text-gray-300 mt-6 opacity-70"
            style={{ 
              fontFamily: "Avenir",
              opacity: showOptions ? 0.7 : 0,
              transition: 'opacity 1.5s ease-out 0.5s'
            }}
          >
            Choose your next step to continue the mystery adventure
          </p>
        </div>
      </div>
    </div>
  );
}